import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuoteCatalog, useSetQuoteCatalogCategory } from '../../hooks/useApi';

const categoryOptions = [
  { value: '', label: '— Ingen —' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'CONTAINER', label: 'Container' },
  { value: 'BIN', label: 'Spand/Beholder' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'MISC', label: 'Diverse' },
];

export default function QuoteCatalogSettings() {
  const navigate = useNavigate();
  const { data: items = [], isLoading } = useQuoteCatalog();
  const setCategory = useSetQuoteCatalogCategory();
  const [filter, setFilter] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const filtered = filter
    ? items.filter(i =>
        i.name.toLowerCase().includes(filter.toLowerCase()) ||
        i.productNumber.toLowerCase().includes(filter.toLowerCase())
      )
    : items;

  async function handleCategoryChange(doraProductId: string, category: string) {
    try {
      await setCategory.mutateAsync({ doraProductId, category });
      setSnackbar({ open: true, message: 'Kategori gemt', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Fejl', severity: 'error' });
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate('/settings')}>Tilbage</Button>
        <Typography variant="h5" fontWeight={600}>Tilbudskatalog — produktkategorier</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Kategorisér DORA-produkter, så de kan vælges fra det rigtige sted i tilbudsformularen.
        Produkter kan rettes ét sted i DORA — kategori-mapping bor lokalt her.
      </Typography>

      <TextField
        placeholder="Søg produkt..."
        size="small"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Produktnr.</TableCell>
                <TableCell>Navn</TableCell>
                <TableCell align="right">Standardpris</TableCell>
                <TableCell sx={{ width: 200 }}>Kategori</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.productNumber}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">
                    {item.salesPrice ? new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(parseFloat(item.salesPrice)) : '—'}
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={item.category || ''}
                        onChange={(e) => handleCategoryChange(item.id, e.target.value as string)}
                      >
                        {categoryOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
